import React, { useState, useEffect } from 'react';
import {
  Mail,
  Send,
  Users,
  Settings,
  TestTube,
  AlertCircle,
  CheckCircle,
  Loader,
} from 'lucide-react';

const EmailManager = () => {
  const [activeTab, setActiveTab] = useState('configuration');
  const [emailStatus, setEmailStatus] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Configuration state
  const [config, setConfig] = useState({
    emailProvider: 'mailerlite',
    emailApiKey: '',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    emailUser: '',
    emailPass: '',
    emailFrom: '',
  });

  // Test email state
  const [testEmail, setTestEmail] = useState({
    to: '',
    subject: 'Test Email from SWARM',
    html: '<h2>Test Email</h2><p>This is a test email from SWARM Incident Reporter.</p>',
    text: 'Test Email\n\nThis is a test email from SWARM Incident Reporter.',
  });

  // Campaign state
  const [campaign, setCampaign] = useState({
    subject: '',
    content: '',
    groupId: '',
  });

  const [groups, setGroups] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    loadEmailStatus();
    loadSMTPStatus();
  }, []);

  const loadEmailStatus = async () => {
    try {
      const response = await fetch('/api/email/status', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });
      const data = await response.json();
      setEmailStatus(data);
    } catch (error) {
      console.error('Error loading email status:', error);
    }
  };

  const loadSMTPStatus = async () => {
    try {
      const response = await fetch('/api/email/smtp/status', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });
      const data = await response.json();
      setSmtpStatus(data);
    } catch (error) {
      console.error('Error loading SMTP status:', error);
    }
  };

  const testSMTPConnection = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/email/smtp/test', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });
      const data = await response.json();
      if (data.valid) {
        setSuccess('SMTP connection successful!');
        setSmtpStatus(data);
      } else {
        setError(`SMTP connection failed: ${data.error}`);
      }
    } catch (error) {
      setError(`Error testing SMTP: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.to) {
      setError('Please enter a recipient email address');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/email/smtp/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
        body: JSON.stringify(testEmail),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Test email sent successfully!');
      } else {
        setError(`Failed to send test email: ${data.error}`);
      }
    } catch (error) {
      setError(`Error sending test email: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await fetch('/api/email/groups', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadSubscribers = async groupId => {
    try {
      const response = await fetch(`/api/email/groups/${groupId}/subscribers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });
      const data = await response.json();
      setSubscribers(data.subscribers || []);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    }
  };

  const createCampaign = async () => {
    if (!campaign.subject || !campaign.content || !campaign.groupId) {
      setError('Please fill in all campaign fields');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/email/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
        body: JSON.stringify(campaign),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Campaign created successfully!');
        setCampaign({ subject: '', content: '', groupId: '' });
      } else {
        setError(`Failed to create campaign: ${data.error}`);
      }
    } catch (error) {
      setError(`Error creating campaign: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = status => {
    if (status?.valid) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (status?.error) {
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    } else {
      return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = status => {
    if (status?.valid) {
      return 'Connected';
    } else if (status?.error) {
      return 'Error';
    } else {
      return 'Not Configured';
    }
  };

  const getStatusColor = status => {
    if (status?.valid) {
      return 'text-green-600';
    } else if (status?.error) {
      return 'text-red-600';
    } else {
      return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Mail className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Email Management</h2>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {getStatusIcon(emailStatus)}
              <span className={`text-sm font-medium ${getStatusColor(emailStatus)}`}>
                Email Service: {getStatusText(emailStatus)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {getStatusIcon(smtpStatus)}
              <span className={`text-sm font-medium ${getStatusColor(smtpStatus)}`}>
                SMTP: {getStatusText(smtpStatus)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'configuration', label: 'Configuration', icon: Settings },
            { id: 'testing', label: 'Testing', icon: TestTube },
            { id: 'campaigns', label: 'Campaigns', icon: Send },
            { id: 'subscribers', label: 'Subscribers', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">{success}</span>
            </div>
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Email Service Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Provider
                  </label>
                  <select
                    value={config.emailProvider}
                    onChange={e => setConfig({ ...config, emailProvider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="mailerlite">MailerLite</option>
                    <option value="mailchimp">Mailchimp</option>
                    <option value="sendgrid">SendGrid</option>
                    <option value="convertkit">ConvertKit</option>
                    <option value="activecampaign">ActiveCampaign</option>
                    <option value="klaviyo">Klaviyo</option>
                    <option value="getresponse">GetResponse</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                  <input
                    type="password"
                    value={config.emailApiKey}
                    onChange={e => setConfig({ ...config, emailApiKey: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter API key"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={config.smtpHost}
                    onChange={e => setConfig({ ...config, smtpHost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                  <input
                    type="number"
                    value={config.smtpPort}
                    onChange={e => setConfig({ ...config, smtpPort: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="587"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email User</label>
                  <input
                    type="email"
                    value={config.emailUser}
                    onChange={e => setConfig({ ...config, emailUser: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Password
                  </label>
                  <input
                    type="password"
                    value={config.emailPass}
                    onChange={e => setConfig({ ...config, emailPass: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="App password or account password"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
                  <input
                    type="email"
                    value={config.emailFrom}
                    onChange={e => setConfig({ ...config, emailFrom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="SWARM Reporter <noreply@swarmreporter.com>"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.smtpSecure}
                      onChange={e => setConfig({ ...config, smtpSecure: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Use SSL/TLS (port 465)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={testSMTPConnection}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                Test SMTP Connection
              </button>
            </div>
          </div>
        )}

        {/* Testing Tab */}
        {activeTab === 'testing' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Test Email</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Email</label>
                  <input
                    type="email"
                    value={testEmail.to}
                    onChange={e => setTestEmail({ ...testEmail, to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="test@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={testEmail.subject}
                    onChange={e => setTestEmail({ ...testEmail, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTML Content
                  </label>
                  <textarea
                    value={testEmail.html}
                    onChange={e => setTestEmail({ ...testEmail, html: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Content
                  </label>
                  <textarea
                    value={testEmail.text}
                    onChange={e => setTestEmail({ ...testEmail, text: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={sendTestEmail}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Email Campaigns</h3>
              <button
                onClick={loadGroups}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Load Groups
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={campaign.subject}
                  onChange={e => setCampaign({ ...campaign, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Campaign subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                <select
                  value={campaign.groupId}
                  onChange={e => setCampaign({ ...campaign, groupId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a group</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.total} subscribers)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <textarea
                  value={campaign.content}
                  onChange={e => setCampaign({ ...campaign, content: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="HTML content for the campaign"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={createCampaign}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Create Campaign
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Subscribers</h3>
              <div className="flex space-x-2">
                <select
                  onChange={e => loadSubscribers(e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a group to view subscribers</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={loadGroups}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  Load Groups
                </button>
              </div>
            </div>

            {subscribers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fields
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscribers.map(subscriber => (
                      <tr key={subscriber.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {subscriber.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              subscriber.status === 'active' || subscriber.status === 'subscribed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {subscriber.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {subscriber.fields ? JSON.stringify(subscriber.fields) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No subscribers loaded. Select a group to view subscribers.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailManager;
